/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import {
  BoxBufferGeometry,
  FrontSide,
  Mesh,
} from 'three';

import {
  CapNames,
  DefaultCaps,
  Director,
  Materials,
  Stage,
  DefaultNavigators,
  FloorKind,
  FloorDesc,
  CapDataDesc,
} from '@sick-davinci/glow';

/**
 * Demo:    Navigation
 * Description:
 *   This demo application shows all available Navigation types and how they can be switched
 * Features:
 *  - Custom Navigations
 *  - Capability handling
 *  - Native threejs code
 */
export function run(viewParent: HTMLElement) {

  const stage = new Stage(viewParent, {
    cameras: [{
      name: 'primary',
      from: [0, 5, 20],
      to: [0, 5, 0],
      up: [0, 1, 0],
      fov: 80,
      pivot: [0, 0, 0],
    }, {
      name: 'secondary',
      from: [0, 0, 30],
      to: [0, 0, 0],
      up: [0, 1, 0],
      fov: 80,
      pivot: [0, 0, 0],
    }],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'primary',
      scene: 'default',
      camera: 'primary',
      navigator: 'orbit',
      left: 0,
      top: 0,
      width: .5,
      height: 1.0,
      bgcolor: 0x2070b0,
    }, {
      name: 'secondary',
      scene: 'default',
      camera: 'secondary',
      navigator: 'firstPerson',
      left: 0.5,
      top: 0,
      width: .5,
      height: 1.0,
      bgcolor: 0xb02070,
    }],
  });

  // * Add Navigators
  DefaultNavigators.initOrbit(stage, 'orbit');

  DefaultNavigators.initFirstPerson(stage, 'firstPerson');

  DefaultNavigators.init2D(stage, '2D', {pan: {speed: 5}});

  // * Data handling using capabilities
  const director = new Director(stage);

  DefaultCaps.init(director);

  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xfffff0, orientation: { position: [1500, 800, -1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0x888888 });
  director.handle(CapNames.AXIS, { axisLength: 10 });

  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.CHECKERBOARD,
      size: 256,
      segments: 128,
    },
    orientation: {
      position: [0, -.01, 0],
    },
  } as CapDataDesc<FloorDesc>);

  // * Add 3 boxes using native threejs code
  stage.applyThreeDirect((scene) => {

    scene.add(createBox(0xf20000, [10, 10, 10], [-10, 5, 0]));
    scene.add(createBox(0xf2f200, [5, 20, 5], [10, 10, -5]));
    scene.add(createBox(0x0000f2, [10, 10, 10], [10, 5, -15]));

  });

  stage.start();  // start visualizing data

  return {
    stage,
  };

} // end of setup()

function createBox(color: number, size: [number, number, number], position: [number, number, number]) {
  const box =
    new Mesh(
      new BoxBufferGeometry(...size),
      Materials.create({ type: 'standard', params: { color, side: FrontSide } }),
    );
  box.position.set(...position);
  box.updateMatrix();
  return box;
}
